import { useCallback, useRef } from 'react';

function useUndoRedo<T>(historyLimit = 200) {
  const undoStack = useRef<T[]>([]);
  const redoStack = useRef<T[]>([]);
  const activeVersion = useRef<T>();

  const canUndo = undoStack.current.length > 0;
  const canRedo = redoStack.current.length > 0;

  const addToUndoStack = useCallback(
    (payload: T) => {
      if (activeVersion.current === undefined) {
        // We have nothing in our stack yet
        activeVersion.current = payload;
      } else {
        // Move the currentVersion onto the undo stack
        undoStack.current.push(JSON.parse(JSON.stringify(activeVersion.current)));

        // Replace the current version with the latest saved version
        activeVersion.current = payload;

        // To prevent memory craziness we cap history to the specified limit
        if (undoStack.current.length > historyLimit) {
          undoStack.current.splice(0, 1);
        }
      }
    },
    [historyLimit],
  );

  const addToRedoStack = useCallback((payload: T) => {
    redoStack.current.push(payload);
  }, []);

  const handleUndo = useCallback((): T | undefined => {
    if (canUndo) {
      // Get the last item in the undo stack
      const lastAddedItem = undoStack.current.pop();

      if (lastAddedItem && activeVersion.current) {
        // Add to redo stack
        addToRedoStack({ ...activeVersion.current });
        activeVersion.current = lastAddedItem;
      }
      return lastAddedItem;
    } else {
      return undefined;
    }
  }, [addToRedoStack, canUndo]);

  const handleRedo = useCallback((): T | undefined => {
    if (canRedo) {
      const lastAddedItem = redoStack.current.pop();
      if (lastAddedItem && activeVersion.current) {
        addToUndoStack({ ...activeVersion.current });
        activeVersion.current = lastAddedItem;
      }
      return lastAddedItem;
    } else {
      return undefined;
    }
  }, [addToUndoStack, canRedo]);

  return {
    redo: handleRedo,
    undo: handleUndo,
    addToUndoStack,
    canRedo,
    canUndo,
  };
}

export default useUndoRedo;
